import { lazy } from "preact/compat";
import type { ComponentType } from "preact";
import { report } from "./telemetry";

const KEY = "op.chunkReloadAt";
/** A second failure within this window is a real error, not a stale app, so it is not reloaded again. */
const RETRY_WINDOW_MS = 30_000;

/**
 * lazy(), but when the chunk fails to load the page reloads once to pick up the current version.
 *
 * Every deploy renames the lazily loaded chunks (Vite hashes them) and the new service worker takes
 * over at once, so a copy of the app that was already open keeps running old code that asks for
 * chunk names the new deployment no longer has. Without this, the sheet or screen that needed the
 * chunk (the account sheet, the workshop, the tour) silently never appears. A reload loads the
 * current shell, whose chunk names exist. Guarded by a timestamp in sessionStorage so a chunk that
 * is genuinely broken cannot cause a reload loop.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(load: () => Promise<{ default: T }>): T {
  // preact/compat types lazy() through a conditional type that TypeScript cannot narrow back to T.
  return lazy<T>(() =>
    load().catch((err: unknown): Promise<{ default: T }> => {
      let last = 0;
      try {
        last = Number(sessionStorage.getItem(KEY)) || 0;
      } catch {
        /* storage unavailable: fall through and reload once */
      }
      if (Date.now() - last > RETRY_WINDOW_MS) {
        try {
          sessionStorage.setItem(KEY, String(Date.now()));
        } catch {
          /* ignore */
        }
        try {
          report("chunk.reload", err instanceof Error ? err.message : String(err), {});
        } catch {
          /* ignore */
        }
        location.reload();
        // Never settles: the page is going away.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }),
  ) as unknown as T;
}
